import { useTranslation } from 'react-i18next';
import { Menu, X } from '@/components/icons';
import { BrandLogo } from '@/components/branding/BrandLogo';

/**
 * Mobile-only top bar — only renders below `md` per
 * `sustevo-handbook/brand/APP_SHELL.md` § 8.
 *
 * Matches PM canonical (`Sustevo-Core/project-management-tool/src/components/AppShell.tsx`
 * lines 159-172) exactly: hamburger toggle on the left styled as an
 * outlined icon button, then the brand mark + product name in prominent
 * `text-sm font-semibold tracking-tight ink-900` styling. The hamburger
 * flips between List and X glyphs per `aria-expanded`.
 *
 * The toggle is a plain `<button>` rather than the shadcn `<Button>`
 * wrapper because PM's canonical uses the bare element — keeping the
 * markup byte-equivalent makes cross-app visual regression baselines
 * stable.
 */
export interface MobileTopBarProps {
  open: boolean;
  onToggle: () => void;
}

export function MobileTopBar({ open, onToggle }: MobileTopBarProps): JSX.Element {
  const { t } = useTranslation();
  const label = open
    ? t('topbar.mobileNav.close', { defaultValue: 'Close navigation' })
    : t('topbar.mobileNav.open', { defaultValue: 'Open navigation' });
  const productLabel = t('app.brandLabel', { defaultValue: 'SENVIRO' });

  return (
    // BRAND.md § 3.6 token discipline — chrome reads the canonical ink scale
    // directly (light/dark pair) instead of the shadcn `border-border` /
    // `bg-card` / `text-foreground` / `bg-muted` aliases.
    <div className="flex items-center gap-3 border-b border-[var(--color-ink-200)] bg-[var(--color-ink-0)] px-4 py-2 dark:border-[var(--color-ink-700)] dark:bg-[var(--color-ink-800)] md:hidden">
      <button
        type="button"
        data-testid="mobile-nav-toggle"
        data-open={open ? 'true' : 'false'}
        onClick={onToggle}
        aria-label={label}
        aria-expanded={open}
        aria-controls="app-shell-sidebar"
        className="inline-flex items-center justify-center rounded-md border border-[var(--color-ink-200)] bg-[var(--color-ink-0)] p-2 text-[var(--color-ink-900)] hover:bg-[var(--color-ink-50)] dark:border-[var(--color-ink-700)] dark:bg-[var(--color-ink-800)] dark:text-[var(--color-ink-100)] dark:hover:bg-[var(--color-ink-700)]"
      >
        {open ? (
          <X className="h-[18px] w-[18px]" aria-hidden="true" />
        ) : (
          <Menu className="h-[18px] w-[18px]" aria-hidden="true" />
        )}
      </button>
      <span className="flex items-center gap-2">
        <BrandLogo variant="color-mark" height={20} />
        <span className="text-sm font-semibold tracking-tight text-[var(--color-ink-900)] dark:text-[var(--color-ink-100)]">
          {productLabel}
        </span>
      </span>
    </div>
  );
}
